import React, { useCallback, useMemo, useState } from 'react';
import { IDerivedFieldDefinitionMeta } from '../../../../utils/apollo/graphqlSchema';
import GeneralFormItem from '../../../../components/GeneralFormItem';
import KubitTooltip from '../../../../components/KubitTooltip';
import WarningModal from '../../../../components/ConfirmationModal/WarningModal';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  isBold: boolean;
  isDisabled: boolean;
  definition: IDerivedFieldDefinitionMeta;
  canEdit: boolean;
  requireEditConfirmation: boolean;
  onEditClick: () => void;
}

const getDefinitionText = (definition: IDerivedFieldDefinitionMeta) => {
  switch (definition.__typename) {
    case 'HardCodedDerivedFieldMeta':
      return definition.description;
    case 'RawSqlDerivedFieldMeta':
      return `SQL: ${definition.rawSql}`;
    default:
      throw new UnreachableCaseError(definition);
  }
};

const DataModelerDerivedFieldDefinitionMeta = ({
  isBold,
  isDisabled,
  definition,
  canEdit,
  requireEditConfirmation,
  onEditClick,
}: IProps) => {
  const text = getDefinitionText(definition);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const handleConfirmWarning = useCallback(() => {
    setConfirmationModalOpen(false);
    onEditClick();
  }, [onEditClick]);

  const handleCancelWarning = useCallback(() => {
    setConfirmationModalOpen(false);
  }, []);

  const handleEditClick = useMemo(() => {
    if (canEdit) {
      return () => {
        if (!requireEditConfirmation) {
          onEditClick();
        } else {
          setConfirmationModalOpen(true);
        }
      };
    }
    return undefined;
  }, [canEdit, onEditClick, requireEditConfirmation]);

  return (
    <>
      <GeneralFormItem
        key={'definition'}
        isBold={isBold}
        isDisabled={isDisabled}
        label={
          <KubitTooltip
            noOpenDelay={true}
            content={
              canEdit
                ? 'Click to edit this Derived Field definition'
                : undefined
            }
          >
            <div>{text}</div>
          </KubitTooltip>
        }
        onClick={handleEditClick}
      />
      {confirmationModalOpen && (
        <WarningModal
          onConfirm={handleConfirmWarning}
          onCancel={handleCancelWarning}
          trackContext="data modeler"
          trackConfirmAction="edit derived field - confirm"
          trackCancelAction="edit derived field - cancel"
        >
          Editing an already published field definition can alter the results
          from existing reports when re-executed. Are you sure you want to
          continue?
        </WarningModal>
      )}
    </>
  );
};

export default DataModelerDerivedFieldDefinitionMeta;
